/**
 * Route tree (code-based TanStack Router).
 *
 *   /sign-in, /sign-up                 public auth pages
 *   /verify-email                      confirm a signed-in account's email (required before /_authed)
 *   /_authed/invitations/$invitationId accept an emailed organization invitation
 *   /s/$appSlug[/$channelSlug]         public subscription page (QR target)
 *   /_authed                           session + verified-email guard (pathless)
 *     /                                → first organization or onboarding
 *     /onboarding
 *     /$orgSlug                        organization shell (membership checked by the API)
 *       /, apps, apps/$appSlug/…, developers, settings[/account|members|billing|api-keys]
 */
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  type RouteComponent,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";
import { ApplicationLayout, ApplicationPending } from "@/features/applications/ApplicationLayout.tsx";
import { AuthLayoutPending } from "@/features/auth/AuthLayout.tsx";
import { AppShell, AppShellPending } from "@/features/shell/AppShell.tsx";
import { DayKey } from "@/lib/day-range.ts";
import {
  apiKeysQuery,
  applicationQuery,
  applicationsQuery,
  billingQuery,
  channelsQuery,
  invitationsQuery,
  membersQuery,
  notificationQuery,
  organizationQuery,
  organizationsQuery,
  overviewQuery,
  sessionQuery,
} from "@/lib/queries.ts";
import {
  AccountSettingsPending,
  ApiKeysPending,
  ApplicationOverviewPending,
  ApplicationsPending,
  AppPending,
  BillingPending,
  ChannelsPending,
  ComposerPending,
  ContentPending,
  DevelopersPending,
  MembersPending,
  NotificationDetailPending,
  NotificationsPending,
  OrganizationSettingsPending,
  OverviewPending,
  PassthroughPending,
  SubscribersPending,
  TabPending,
} from "./route-pending.tsx";
import { PageError, PageNotFound, RouteError, RouteNotFound } from "./route-states.tsx";
import { RootLayout } from "./root-layout.tsx";

// Pages are code-split per route; the shell and application frame load eagerly.
const ApiKeysPage = lazyRouteComponent(() => import("@/features/api-keys/ApiKeysPage.tsx"), "ApiKeysPage");
const ApplicationOverviewPage = lazyRouteComponent(
  () => import("@/features/applications/ApplicationOverviewPage.tsx"),
  "ApplicationOverviewPage",
);
const ApplicationSettingsPage = lazyRouteComponent(
  () => import("@/features/applications/ApplicationSettingsPage.tsx"),
  "ApplicationSettingsPage",
);
const ApplicationsPage = lazyRouteComponent(
  () => import("@/features/applications/ApplicationsPage.tsx"),
  "ApplicationsPage",
);
const SignInPage = lazyRouteComponent(() => import("@/features/auth/SignInPage.tsx"), "SignInPage");
const SignUpPage = lazyRouteComponent(() => import("@/features/auth/SignUpPage.tsx"), "SignUpPage");
const VerifyEmailPage = lazyRouteComponent(() => import("@/features/auth/VerifyEmailPage.tsx"), "VerifyEmailPage");
const ChannelsPage = lazyRouteComponent(() => import("@/features/channels/ChannelsPage.tsx"), "ChannelsPage");
const DevelopersPage = lazyRouteComponent(() => import("@/features/developers/DevelopersPage.tsx"), "DevelopersPage");
const ComposerPage = lazyRouteComponent(() => import("@/features/notifications/ComposerPage.tsx"), "ComposerPage");
const NotificationDetailPage = lazyRouteComponent(
  () => import("@/features/notifications/NotificationDetailPage.tsx"),
  "NotificationDetailPage",
);
const NotificationsPage = lazyRouteComponent(
  () => import("@/features/notifications/NotificationsPage.tsx"),
  "NotificationsPage",
);
const OnboardingPage = lazyRouteComponent(() => import("@/features/onboarding/OnboardingPage.tsx"), "OnboardingPage");
const BillingPage = lazyRouteComponent(() => import("@/features/organization/BillingPage.tsx"), "BillingPage");
const AcceptInvitationPage = lazyRouteComponent(
  () => import("@/features/organization/AcceptInvitationPage.tsx"),
  "AcceptInvitationPage",
);
const MembersPage = lazyRouteComponent(() => import("@/features/organization/MembersPage.tsx"), "MembersPage");
const OrganizationSettingsPage = lazyRouteComponent(
  () => import("@/features/organization/OrganizationSettingsPage.tsx"),
  "OrganizationSettingsPage",
);
const AccountSettingsPage = lazyRouteComponent(
  () => import("@/features/account/AccountSettingsPage.tsx"),
  "AccountSettingsPage",
);
const OverviewPage = lazyRouteComponent(() => import("@/features/overview/OverviewPage.tsx"), "OverviewPage");
const SubscribePage = lazyRouteComponent(() => import("@/features/public/SubscribePage.tsx"), "SubscribePage");
const SubscribersPage = lazyRouteComponent(
  () => import("@/features/subscribers/SubscribersPage.tsx"),
  "SubscribersPage",
);

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: PageError,
  notFoundComponent: PageNotFound,
});

const redirectSearch = z.object({ redirect: z.string().startsWith("/").optional() });

/** `error`: set by Better Auth when a GitHub sign-in fails (see `GitHubSignInButton`). */
const signInSearch = redirectSearch.extend({ error: z.string().max(64).optional().catch(undefined) });

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  validateSearch: signInSearch,
  component: SignInPage,
  pendingComponent: AuthLayoutPending,
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-up",
  validateSearch: redirectSearch,
  component: SignUpPage,
  pendingComponent: AuthLayoutPending,
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  validateSearch: redirectSearch,
  beforeLoad: async ({ context, location, search }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (!session) throw redirect({ to: "/sign-in", search: { redirect: location.href } });
    if (session.user.emailVerified) throw redirect({ to: search.redirect ?? "/" });
    return { session };
  },
  component: VerifyEmailPage,
  pendingComponent: AuthLayoutPending,
});

/** `t`: personal subscribe token from `POST /v1/subscribe-links`. */
const subscribeSearch = z.object({ t: z.string().max(256).optional().catch(undefined) });

const subscribeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/s/$appSlug",
  validateSearch: subscribeSearch,
  component: SubscribePage,
});

const subscribeChannelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/s/$appSlug/$channelSlug",
  validateSearch: subscribeSearch,
  component: SubscribePage,
});

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_authed",
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (!session) throw redirect({ to: "/sign-in", search: { redirect: location.href } });
    // Password sign-ups confirm their email first: GitHub links to an existing account by
    // email only once it's verified.
    if (!session.user.emailVerified) throw redirect({ to: "/verify-email", search: { redirect: location.href } });
    return { session };
  },
  // Checking the session on a cold load: show the skeleton of where it's headed
  // straight away, taking over from the boot screen in index.html without a
  // blank frame. Once mounted this match is retained, so in-app navigations
  // never show it.
  pendingComponent: PassthroughPending,
  pendingMs: 0,
});

const homeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/",
  beforeLoad: async ({ context }) => {
    const orgs = await context.queryClient.ensureQueryData(organizationsQuery);
    const first = orgs[0];
    if (!first) throw redirect({ to: "/onboarding" });
    throw redirect({ to: "/$orgSlug", params: { orgSlug: first.slug } });
  },
});

const onboardingRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/onboarding",
  component: OnboardingPage,
});

/** Emailed invitation links land here; the guard above sends new recipients through sign-up first. */
const acceptInvitationRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/invitations/$invitationId",
  component: AcceptInvitationPage,
  pendingComponent: AuthLayoutPending,
});

const orgRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "$orgSlug",
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(organizationQuery(params.orgSlug)),
      context.queryClient.ensureQueryData(organizationsQuery),
      context.queryClient.ensureQueryData(applicationsQuery(params.orgSlug)),
    ]);
  },
  component: AppShell,
  // The shell didn't render, so these stand on their own; unknown URLs below it stay inside the shell.
  errorComponent: PageError,
  notFoundComponent: RouteNotFound,
  pendingComponent: AppShellPending,
});

const orgChild = <P extends string>(path: P, pendingComponent: RouteComponent = ContentPending) => ({
  getParentRoute: () => orgRoute,
  path,
  errorComponent: RouteError,
  pendingComponent,
});

const overviewRoute = createRoute({
  ...orgChild("/", OverviewPending),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(overviewQuery(params.orgSlug)),
  component: OverviewPage,
});

const applicationsRoute = createRoute({ ...orgChild("apps", ApplicationsPending), component: ApplicationsPage });

const applicationRoute = createRoute({
  ...orgChild("apps/$appSlug"),
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(applicationQuery(params.orgSlug, params.appSlug)),
  component: ApplicationLayout,
  pendingComponent: ApplicationPending,
});

const appChild = <P extends string>(path: P, pendingComponent: RouteComponent = TabPending) => ({
  getParentRoute: () => applicationRoute,
  path,
  errorComponent: RouteError,
  pendingComponent,
});

const applicationOverviewRoute = createRoute({
  ...appChild("/", ApplicationOverviewPending),
  component: ApplicationOverviewPage,
});
const notificationsRoute = createRoute({
  ...appChild("notifications", NotificationsPending),
  // Local days, both ends included; a malformed one is dropped rather than failing the page.
  validateSearch: z.object({ from: DayKey.optional().catch(undefined), to: DayKey.optional().catch(undefined) }),
  component: NotificationsPage,
});
const composerRoute = createRoute({
  ...appChild("notifications/new", ComposerPending),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(channelsQuery(params.orgSlug, params.appSlug)),
  component: ComposerPage,
});
const notificationDetailRoute = createRoute({
  ...appChild("notifications/$notificationId", NotificationDetailPending),
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(notificationQuery(params.orgSlug, params.appSlug, params.notificationId)),
  component: NotificationDetailPage,
});
const subscribersRoute = createRoute({
  ...appChild("subscribers", SubscribersPending),
  validateSearch: z.object({
    status: z.enum(["active", "paused", "revoked"]).optional(),
    channel: z.string().optional(),
  }),
  component: SubscribersPage,
});
const channelsRoute = createRoute({
  ...appChild("channels", ChannelsPending),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(channelsQuery(params.orgSlug, params.appSlug)),
  component: ChannelsPage,
});
const applicationSettingsRoute = createRoute({ ...appChild("settings"), component: ApplicationSettingsPage });

const apiKeysRoute = createRoute({
  ...orgChild("settings/api-keys", ApiKeysPending),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(apiKeysQuery(params.orgSlug)),
  component: ApiKeysPage,
});
const developersRoute = createRoute({ ...orgChild("developers", DevelopersPending), component: DevelopersPage });
const membersRoute = createRoute({
  ...orgChild("settings/members", MembersPending),
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(membersQuery(params.orgSlug)),
      context.queryClient.ensureQueryData(invitationsQuery(params.orgSlug)),
    ]),
  component: MembersPage,
});
const billingRoute = createRoute({
  ...orgChild("settings/billing", BillingPending),
  validateSearch: z.object({ checkout: z.string().optional() }),
  loader: ({ context, params }) => context.queryClient.ensureQueryData(billingQuery(params.orgSlug)),
  component: BillingPage,
});
const organizationSettingsRoute = createRoute({
  ...orgChild("settings", OrganizationSettingsPending),
  component: OrganizationSettingsPage,
});
const accountSettingsRoute = createRoute({
  ...orgChild("settings/account", AccountSettingsPending),
  component: AccountSettingsPage,
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  signUpRoute,
  verifyEmailRoute,
  subscribeRoute,
  subscribeChannelRoute,
  authedRoute.addChildren([
    homeRoute,
    onboardingRoute,
    acceptInvitationRoute,
    orgRoute.addChildren([
      overviewRoute,
      applicationsRoute,
      applicationRoute.addChildren([
        applicationOverviewRoute,
        notificationsRoute,
        composerRoute,
        notificationDetailRoute,
        subscribersRoute,
        channelsRoute,
        applicationSettingsRoute,
      ]),
      apiKeysRoute,
      developersRoute,
      membersRoute,
      billingRoute,
      organizationSettingsRoute,
      accountSettingsRoute,
    ]),
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    // Data freshness is TanStack Query's job; the router just triggers loaders.
    defaultPreloadStaleTime: 0,
    // Skeletons appear quickly on slow loads, then hold long enough not to flicker.
    defaultPendingComponent: AppPending,
    defaultPendingMs: 200,
    defaultPendingMinMs: 400,
    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
